(() => {
  'use strict';

  if (window.__miniamiProductPageRuntime) return;
  window.__miniamiProductPageRuntime = true;

  const productInstances = new WeakMap();
  const recommendationInstances = new WeakMap();
  const PRODUCT_SELECTOR = 'product-info[data-miniami-product-page]';
  const RECOMMENDATION_SELECTOR = 'product-recommendations[data-recommendation-carousel="true"]';

  function motionAllowed() {
    const reduced = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    const saveData = Boolean(navigator.connection?.saveData);
    const body = document.body;
    const motionLevel = body?.dataset.miniamiMotionLevel || body?.dataset.miniamiMotion || '';
    const globalOff = motionLevel.toLowerCase() === 'off';
    const forcedStatic = document.documentElement.dataset.miniamiMotionForcedStatic === 'true';
    const globallyPaused = document.documentElement.dataset.miniamiMotionPaused === 'true';
    return !reduced && !saveData && !globalOff && !forcedStatic && !globallyPaused;
  }

  function addListener(target, type, handler, options, cleanups, signal) {
    if (!target?.addEventListener) return;
    if (signal) {
      target.addEventListener(type, handler, { ...options, signal });
    } else {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }
  }

  function productRootFor(target) {
    if (target?.matches?.(PRODUCT_SELECTOR)) return target;
    return target?.querySelector?.(PRODUCT_SELECTOR) || null;
  }

  function recommendationRootFor(target) {
    if (target?.matches?.(RECOMMENDATION_SELECTOR)) return target;
    return target?.querySelector?.(RECOMMENDATION_SELECTOR) || null;
  }

  function primaryForm(root, sticky) {
    const configuredId = sticky?.dataset.productFormId;
    if (configuredId) {
      const configured = Array.from(root.querySelectorAll('form')).find((form) => form.getAttribute('id') === configuredId);
      if (configured?.matches('form[data-type="add-to-cart-form"]')) return configured;
    }
    return root.querySelector('form[data-type="add-to-cart-form"]')
      || root.querySelector('form[id^="product-form-"]:not([id*="-installment-"])');
  }

  function primarySubmit(form) {
    return form?.querySelector('button[name="add"], .product-form__submit') || null;
  }

  function submitLabel(button) {
    return button?.querySelector('span:not(.loading__spinner)')?.textContent?.trim()
      || button?.textContent?.trim()
      || '';
  }

  function variantState(root, form, button) {
    const strings = window.variantStrings || {};
    const label = submitLabel(button);
    const unavailable = label === strings.unavailable || button?.dataset.miniamiSubmitState === 'unavailable';
    const soldOut = !unavailable && (label === strings.soldOut || button?.dataset.miniamiSubmitState === 'sold-out');
    const busy = !unavailable && !soldOut
      && (Boolean(button?.classList.contains('loading')) || button?.getAttribute('aria-busy') === 'true');
    const available = Boolean(button)
      && !button.disabled
      && button.getAttribute('aria-disabled') !== 'true'
      && !busy
      && !unavailable
      && !soldOut;
    const priceRoot = root.querySelector('[data-miniami-variant-price]');
    const price = (priceRoot?.querySelector('.price--on-sale .price-item--sale')
      || priceRoot?.querySelector('.price__regular .price-item--regular'))?.textContent?.trim() || '';
    return {
      state: unavailable ? 'unavailable' : soldOut ? 'sold-out' : busy ? 'busy' : available ? 'available' : 'unavailable',
      available,
      busy,
      price: available || busy ? price : '',
      compare: available || busy ? (priceRoot?.querySelector('.price--on-sale .price__sale .price-item--regular')?.textContent?.trim() || '') : '',
      label: unavailable ? (strings.unavailable || label) : soldOut ? (strings.soldOut || label) : (strings.addToCart || label),
      variantId: form?.querySelector('[name="id"]')?.value || '',
    };
  }

  function headerOffset() {
    const explicit = Number.parseFloat(document.documentElement.style.getPropertyValue('--header-height'));
    if (Number.isFinite(explicit)) return explicit;
    return document.querySelector('.section-header')?.getBoundingClientRect().height || 0;
  }

  function initProduct(root) {
    if (!root || productInstances.has(root)) return;

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const cleanups = [];
    const signal = controller?.signal;
    const sticky = root.querySelector('[data-miniami-sticky-buy]');
    let frame = 0;
    let resizeObserver = null;
    let mutationObserver = null;

    const dispose = () => {
      controller?.abort();
      cleanups.splice(0).forEach((cleanup) => cleanup());
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    };

    productInstances.set(root, { dispose });

    if (!sticky) return;

    const initialForm = primaryForm(root, sticky);
    const initialButton = primarySubmit(initialForm);
    if (!initialForm || !initialButton) return;

    const stickyButton = sticky.querySelector('[data-miniami-sticky-submit]');
    const stickyPrice = sticky.querySelector('[data-miniami-sticky-variant]');
    const stickyCompare = sticky.querySelector('[data-miniami-sticky-compare]');
    const stickyMoney = sticky.querySelector('[data-miniami-sticky-bundle-money]');
    const stickyStatus = sticky.querySelector('[data-miniami-sticky-status]');
    const stickyLabel = sticky.querySelector('[data-miniami-sticky-submit-label-text]');

    function syncVariant() {
      const currentForm = primaryForm(root, sticky);
      const currentButton = primarySubmit(currentForm);
      const current = variantState(root, currentForm, currentButton);
      root.dataset.miniamiVariantId = current.variantId;
      sticky.dataset.stickyState = current.state;
      if (stickyPrice) {
        if (stickyPrice.textContent.trim() !== current.price) stickyPrice.textContent = current.price;
        stickyPrice.hidden = !current.price;
      }
      if (stickyCompare) {
        if (stickyCompare.textContent.trim() !== current.compare) stickyCompare.textContent = current.compare;
        stickyCompare.hidden = !current.compare || current.compare === current.price;
      }
      if (stickyMoney) stickyMoney.hidden = !current.price;
      if (stickyStatus) {
        if (stickyStatus.textContent.trim() !== current.label) stickyStatus.textContent = current.label;
        stickyStatus.hidden = current.available || current.busy;
      }
      if (stickyLabel && stickyLabel.textContent.trim() !== current.label) stickyLabel.textContent = current.label;
      if (stickyButton) {
        stickyButton.disabled = !current.available;
        stickyButton.setAttribute('aria-disabled', current.available ? 'false' : 'true');
        stickyButton.setAttribute('aria-busy', current.busy ? 'true' : 'false');
        const title = sticky.querySelector('strong')?.textContent.trim() || '';
        stickyButton.setAttribute('aria-label', [current.label, title, current.compare && 'Previously '+current.compare, current.price].filter(Boolean).join(' · '));
      }
    }

    function setVisible(visible) {
      sticky.hidden = !visible;
      sticky.classList.toggle('is-active', visible);
      sticky.dataset.stickyVisible = visible ? 'true' : 'false';
    }

    function footerGuard() {
      const footer = document.querySelector('[data-miniami-footer]');
      if (!footer) return true;
      const footerTop = footer.getBoundingClientRect().top;
      const band = Math.max(sticky.offsetHeight || 0, 56) + 12;
      return footerTop >= window.innerHeight - band;
    }

    function update() {
      frame = 0;
      const currentButton = primarySubmit(primaryForm(root, sticky));
      if (!currentButton) {
        setVisible(false);
        return;
      }
      const rect = currentButton.getBoundingClientRect();
      const passed = rect.bottom <= headerOffset();
      // Source PDP purchase stays usable at the footer. source.css releases
      // its owning stacking boundary; legacy consumers retain the old guard.
      setVisible(passed && (root.hasAttribute('data-miniami-source-layout') || footerGuard()));
      syncVariant();
    }

    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    }

    function submitFromSticky(event) {
      event.preventDefault();
      const currentForm = primaryForm(root, sticky);
      const currentButton = primarySubmit(currentForm);
      syncVariant();
      if (stickyButton?.disabled || !currentForm || !currentButton) return;
      if (typeof currentForm.requestSubmit === 'function') currentForm.requestSubmit(currentButton);
      else currentButton.click();
    }

    addListener(stickyButton, 'click', submitFromSticky, {}, cleanups, signal);
    addListener(root, 'change', schedule, {}, cleanups, signal);
    addListener(root, 'input', schedule, {}, cleanups, signal);
    addListener(root, 'variant:change', schedule, {}, cleanups, signal);
    addListener(window, 'scroll', schedule, { passive: true }, cleanups, signal);
    addListener(window, 'resize', schedule, { passive: true }, cleanups, signal);
    addListener(document, 'visibilitychange', schedule, {}, cleanups, signal);

    if (typeof window.ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(root);
    }
    if (typeof window.MutationObserver === 'function') {
      mutationObserver = new MutationObserver((records) => {
        const currentButton = primarySubmit(primaryForm(root, sticky));
        // Native loading/disabled changes do not replace text nodes. Observe
        // those attributes too, but never react to our own sticky mirror.
        if (records.some((record) => record.type === 'childList'
          || (currentButton && (record.target === currentButton || currentButton.contains(record.target))))) schedule();
      });
      mutationObserver.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'aria-busy'] });
    }

    root.dataset.motionAllowed = motionAllowed() ? 'true' : 'false';
    syncVariant();
    update();
  }

  function initRecommendation(section) {
    if (!section || recommendationInstances.has(section)) return;

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const cleanups = [];
    const signal = controller?.signal;
    let shelf = null;
    let resizeObserver = null;
    let contentObserver = null;
    let frame = 0;

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    const dispose = () => {
      controller?.abort();
      cleanups.splice(0).forEach((cleanup) => cleanup());
      shelf?.removeEventListener('scroll', schedule);
      resizeObserver?.disconnect();
      contentObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };

    recommendationInstances.set(section, { dispose });

    function update() {
      frame = 0;
      if (!shelf) return;
      const items = shelf.querySelectorAll('.miniami-recommendation-track--primary > li');
      const controls = section.querySelector('[data-recommendation-controls]');
      const previous = controls?.querySelector('[data-recommendation-previous]');
      const next = controls?.querySelector('[data-recommendation-next]');
      const position = controls?.querySelector('[data-recommendation-position]');
      const max = Math.max(0, shelf.scrollWidth - shelf.clientWidth);
      const scrollable = items.length > 1 && max > 2;
      section.dataset.motionAllowed = motionAllowed() ? 'true' : 'false';
      if (controls) controls.hidden = !scrollable;
      shelf.tabIndex = scrollable ? 0 : -1;
      if (!scrollable) return;
      const rtl = getComputedStyle(shelf).direction === 'rtl';
      const current = Math.max(0, Math.min(max, rtl ? -shelf.scrollLeft : shelf.scrollLeft));
      if (previous) previous.disabled = current <= 2;
      if (next) next.disabled = current >= max - 2;
      if (position && items[0]) {
        const gap = Number.parseFloat(getComputedStyle(items[0].parentElement).columnGap) || 0;
        const stride = items[0].getBoundingClientRect().width + gap;
        const start = Math.min(items.length, Math.floor((current + 2) / stride) + 1);
        const shown = Math.max(1, Math.ceil((shelf.clientWidth + gap) / stride));
        const label = `${start}–${Math.min(items.length, start + shown - 1)} of ${items.length}`;
        if (position.textContent !== label) position.textContent = label;
      }
    }

    function bindContent() {
      const nextShelf = section.querySelector('[data-recommendation-track]');
      if (nextShelf !== shelf) {
        shelf?.removeEventListener('scroll', schedule);
        resizeObserver?.disconnect();
        shelf = nextShelf;
        shelf?.addEventListener('scroll', schedule, { passive: true });
        if (shelf && typeof window.ResizeObserver === 'function') {
          resizeObserver = new ResizeObserver(schedule);
          resizeObserver.observe(shelf);
        }
      }
      schedule();
    }

    function move(direction) {
      if (!shelf) return;
      const first = shelf.querySelector('.miniami-recommendation-track--primary > li');
      if (!first) return;
      const gap = Number.parseFloat(getComputedStyle(first.parentElement).columnGap) || 0;
      const rtl = getComputedStyle(shelf).direction === 'rtl';
      const amount = (first.getBoundingClientRect().width + gap) * direction * (rtl ? -1 : 1);
      shelf.scrollBy({ left: amount, behavior: motionAllowed() ? 'smooth' : 'auto' });
    }

    addListener(section, 'click', (event) => {
      const button = event.target.closest?.('[data-recommendation-previous], [data-recommendation-next]');
      if (!button || !section.contains(button) || button.disabled) return;
      event.preventDefault();
      move(button.hasAttribute('data-recommendation-next') ? 1 : -1);
    }, {}, cleanups, signal);
    addListener(section, 'keydown', (event) => {
      if (event.target !== shelf) return;
      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (!direction && event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();
      if (direction) move(direction);
      else {
        const rtl = getComputedStyle(shelf).direction === 'rtl';
        const max = Math.max(0, shelf.scrollWidth - shelf.clientWidth);
        shelf.scrollTo({ left: (event.key === 'Home' ? 0 : max) * (rtl ? -1 : 1), behavior: motionAllowed() ? 'smooth' : 'auto' });
      }
    }, {}, cleanups, signal);
    addListener(document, 'miniami:motion-change', schedule, {}, cleanups, signal);
    addListener(window, 'resize', schedule, { passive: true }, cleanups, signal);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    addListener(reduced, 'change', schedule, {}, cleanups, signal);

    if (typeof window.MutationObserver === 'function') {
      contentObserver = new MutationObserver(bindContent);
      contentObserver.observe(section, { subtree: true, childList: true });
    }
    bindContent();
  }

  function cleanup(target) {
    const product = productRootFor(target);
    if (product) {
      productInstances.get(product)?.dispose();
      productInstances.delete(product);
    }
    const recommendation = recommendationRootFor(target);
    if (recommendation) {
      recommendationInstances.get(recommendation)?.dispose();
      recommendationInstances.delete(recommendation);
    }
  }

  function initAll(target = document) {
    const productRoots = target.matches?.(PRODUCT_SELECTOR)
      ? [target]
      : Array.from(target.querySelectorAll?.(PRODUCT_SELECTOR) || []);
    productRoots.forEach(initProduct);
    const recommendations = target.matches?.(RECOMMENDATION_SELECTOR)
      ? [target]
      : Array.from(target.querySelectorAll?.(RECOMMENDATION_SELECTOR) || []);
    recommendations.forEach(initRecommendation);
  }

  function observeLifecycle() {
    if (typeof window.MutationObserver !== 'function' || !document.body) return;
    const lifecycleObserver = new MutationObserver((records) => {
      records.forEach(({ addedNodes, removedNodes }) => {
        removedNodes.forEach((node) => {
          if (node.nodeType === 1) cleanup(node);
        });
        addedNodes.forEach((node) => {
          if (node.nodeType === 1) initAll(node);
        });
      });
    });
    lifecycleObserver.observe(document.body, { subtree: true, childList: true });
  }

  observeLifecycle();
  addListener(document, 'shopify:section:load', (event) => initAll(event.target), {}, [], null);
  addListener(document, 'shopify:section:unload', (event) => cleanup(event.target), {}, [], null);
  addListener(document, 'DOMContentLoaded', () => initAll(), { once: true }, [], null);

  if (document.readyState !== 'loading') initAll();
})();